import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldAlert, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, token, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && token) return <Navigate to="/operator" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Authenticated. Welcome, operator.");
      navigate("/operator", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid grid-cols-1 md:grid-cols-2 bg-white">
      {/* Left — brand panel */}
      <div className="relative bg-zinc-950 text-white p-8 md:p-12 flex flex-col justify-between grain-overlay overflow-hidden">
        <div className="flex items-center gap-3" data-testid="brand-mark">
          <div className="w-10 h-10 border-2 border-white grid place-items-center">
            <ShieldAlert className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400">
              Confidential / B2B
            </div>
            <div className="font-display font-black text-xl tracking-tight">ADA INTERCEPTOR</div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <div className="label-xs text-zinc-400 mb-4">Internal Command Center</div>
          <h1 className="font-display text-4xl md:text-5xl font-black leading-[0.95] tracking-tighter mb-6">
            Identify exposure.<br />
            Patch silently.<br />
            <span className="text-red-500">Convert quietly.</span>
          </h1>
          <p className="text-zinc-300 text-base leading-relaxed">
            Single-operator console for ADA digital-accessibility lead generation,
            technical audits and silent-shield deployments.
          </p>
        </div>

        <div className="font-mono text-xs text-zinc-500 relative z-10">
          ── v1.0 / authorised personnel only
        </div>

        {/* deco grid */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      {/* Right — form */}
      <div className="p-8 md:p-12 flex items-center justify-center">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="label-xs mb-2">Authentication</div>
          <h2 className="font-display text-3xl font-black text-zinc-950 mb-8 tracking-tight">
            Secure operator login
          </h2>

          <div className="space-y-5">
            <div>
              <Label htmlFor="email" className="label-xs">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm mt-1 h-11 border-zinc-300 focus-visible:ring-blue-700 font-mono"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-xs">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm mt-1 h-11 border-zinc-300 focus-visible:ring-blue-700 font-mono"
                required
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-sm bg-blue-700 hover:bg-blue-800 text-white font-bold tracking-wide uppercase text-sm"
              data-testid="login-submit-button"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>Authenticate <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
